/**
 * Spinner
 *
 * Central spinner service for overlay and page-level loading indicators.
 */

import { TaskScheduler } from "../../core/task-scheduler";
import { translator } from "../../translation/translator";
import { SpinnerRender } from "./spinner-render";
import { LoadingText } from "../../constants/LoadingText";
import { FormBase } from "../form-base-form";

class SpinnerFormBase extends FormBase {
    public getElement(root: ParentNode | null | undefined, selector: string): Element | null {
        return this.queryElement(root, selector, Element);
    }
}

const spinnerFormBase = new SpinnerFormBase();

interface SpinnerOptions {
    id?: string;
    message?: string | null;
    backdrop?: boolean;
    blur?: boolean;
    zIndex?: number;
    container?: string | Element | null;
    translateKey?: string;
}

interface SpinnerOverlayData {
    overlay: HTMLElement;
    container: Element;
}

interface ButtonLoaderOptions {
    message?: string;
    translateKey?: string;
}

interface SpinnerDefaults {
    message: string;
    backdrop: boolean;
    blur: boolean;
    zIndex: number;
    fadeSpeed: number;
}

export class Spinner {
    private overlays: Map<string, SpinnerOverlayData>;

    private defaultOptions: SpinnerDefaults;

    public constructor() {
        this.overlays = new Map();
        this.defaultOptions = {
            message: LoadingText.LOADING,
            backdrop: true,
            blur: true,
            zIndex: 10000,
            fadeSpeed: 300,
        };
    }

    public show(options: SpinnerOptions = {}): string | void {
        const config: SpinnerDefaults & SpinnerOptions = {
            ...this.defaultOptions,
            ...options,
            translateKey: options.translateKey || "loading",
        };
        const id = config.id || "default";

        if (this.overlays.has(id)) {
            return;
        }

        const overlay = SpinnerRender.createOverlay(config);
        try {
            overlay.setAttribute("data-spinner-id", id);
        } catch (_error) {
            // ignore attribute write failures
        }

        const container = this.resolveContainer(config.container);

        if (id === "page-loader") {
            overlay.classList.add("loading-overlay--page");
        }

        const isInline = Boolean(config.container) && config.backdrop === false && config.blur === false;
        if (isInline) {
            overlay.classList.add("loading-overlay--inline");
        }

        if (id.startsWith("form-")) {
            overlay.classList.add("loading-overlay--form");
        }

        container.appendChild(overlay);
        this.overlays.set(id, { overlay, container });

        if (isInline) {
            overlay.classList.add("show");
        } else {
            requestAnimationFrame(() => {
                overlay.classList.add("show");
            });
        }

        return id;
    }

    public hide(id = "default"): void {
        const spinnerData = this.overlays.get(id);
        if (!spinnerData) {
            return;
        }

        const { overlay, container } = spinnerData;
        overlay.classList.remove("show");

        void TaskScheduler.schedule(() => {
            if (container.contains(overlay)) {
                container.removeChild(overlay);
            }
            this.overlays.delete(id);
        }, "raf");
    }

    public hideAll(): void {
        const ids = Array.from(this.overlays.keys());
        ids.forEach((id) => this.hide(id));
    }

    public isShowing(id = "default"): boolean {
        return this.overlays.has(id);
    }

    public showPageLoader(message: string | null | undefined = undefined): string | void {
        const defaultMessage = translator?.getTranslation("loading_page") || "Loading page...";
        return this.show({
            id: "page-loader",
            message: message === undefined ? defaultMessage : message,
            translateKey: "loading_page",
            backdrop: true,
            blur: true,
            zIndex: 10001,
        });
    }

    public hidePageLoader(): void {
        this.hide("page-loader");
    }

    public showFormLoader(form: HTMLFormElement, message: string | null = null): string | void {
        const id = `form-${Date.now()}`;
        const defaultMessage = translator?.getTranslation("submitting") || "Submitting data...";

        return this.show({
            id,
            message: message || defaultMessage,
            translateKey: "submitting",
            container: form.parentElement,
            backdrop: true,
            blur: false,
            zIndex: 1000,
        });
    }

    public showButtonLoader(button: HTMLButtonElement, options: string | ButtonLoaderOptions | null = null): void {
        if (button.classList.contains("like-btn")) {
            return;
        }

        if (button.hasAttribute("data-original-html")) {
            return;
        }

        button.setAttribute("data-original-html", button.innerHTML);
        button.setAttribute("data-original-disabled", String(button.disabled));
        button.setAttribute("data-original-width-style", button.style.width || "");

        const buttonWidth = Math.ceil(button.getBoundingClientRect().width);
        if (buttonWidth > 0) {
            button.style.width = `${buttonWidth}px`;
        }

        button.disabled = true;
        const btnSpinner = SpinnerRender.createButtonSpinner();

        const normalized: ButtonLoaderOptions | null =
            typeof options === "string"
                ? { message: options, translateKey: "spinner_proccesing" }
                : options && typeof options === "object"
                  ? options
                  : null;

        const translateKey = normalized?.translateKey || "spinner_proccesing";
        const hasExplicitMessage = normalized !== null && Object.prototype.hasOwnProperty.call(normalized, "message");
        const translated = translator?.getTranslation(translateKey);
        const hasValidTranslatedText =
            typeof translated === "string" && translated.trim() !== "" && translated !== translateKey;

        const text = hasExplicitMessage
            ? typeof normalized?.message === "string"
                ? normalized.message
                : ""
            : hasValidTranslatedText
              ? translated
              : "";

        const labelSpan =
            hasExplicitMessage || !hasValidTranslatedText
                ? `<span>${text}</span>`
                : `<span data-translate="${translateKey}">${text}</span>`;

        button.innerHTML = `
            ${btnSpinner.outerHTML}
            ${labelSpan}
        `;
    }

    public hideButtonLoader(button: HTMLButtonElement): void {
        if (!button.hasAttribute("data-original-html")) {
            return;
        }

        button.innerHTML = button.getAttribute("data-original-html") || "";
        button.disabled = button.getAttribute("data-original-disabled") === "true";
        button.style.width = button.getAttribute("data-original-width-style") || "";
        button.removeAttribute("data-original-html");
        button.removeAttribute("data-original-disabled");
        button.removeAttribute("data-original-width-style");
    }

    public async withSpinner<T>(asyncFn: () => Promise<T>, options: SpinnerOptions = {}): Promise<T> {
        const spinnerId = this.show(options);
        try {
            return await asyncFn();
        } finally {
            if (typeof spinnerId === "string") {
                this.hide(spinnerId);
            }
        }
    }

    private resolveContainer(container: string | Element | null | undefined): Element {
        if (typeof container === "string") {
            return spinnerFormBase.getElement(document, container) || document.body;
        }

        if (container instanceof Element) {
            return container;
        }

        return document.body;
    }
}

const spinner = new Spinner();
export { spinner };
